import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';

import MojitoCore from 'mojito/core';
import MojitoModules from 'mojito/modules';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';

import {ApplicationContext} from '#core/utils/context-utils.js';
import {Logger} from '#core/utils/logger.js';
import {merge} from '#core/utils/config-utils.js';

import {selectVisualConfigHash} from '#core/application/modules/visual-config-actions.js';

import {StoreProviderWrapper} from './wrappers/index.js';

const {useConfig} = MojitoCore.Presentation.Hooks;
const {ContextExtender: AnalyticsContextExtender, ContextBuilder: AnalyticsContextBuilder} =
    MojitoCore.Presentation.AnalyticsContext;
const {SkeletonPart} = MojitoPresentation.Components;

const {view: SelectionButtonView, types: SelectionButtonTypes} = MojitoModules.SelectionButton;
const {
    actions: betslipActions,
    selectors: betslipSelectors,
    modelFactory: betslipModelFactory,
} = MojitoServices.Betslip;
const {useEventItem, useEventsLoadDone, useMarketItems, useMarketsLoadDone} = MojitoModules.Common.Hooks;

const SOURCE = 'Widget Selection Button';

const log = Logger(SOURCE);

/**
 * This widget renders a selection button, with inherits existed styles.
 * The button could add/remove the selection to/from the betslip.
 * The button listens to changes in the betslip store, and changes own state correspondingly (active/inactive/pending).
 */

const WidgetSelectionButtonImpl = ({params}) => {
    const {selectionId, marketId, eventId, styles = {}, onChange = () => {}} = params;
    const dispatch = useDispatch();
    const event = useEventItem(eventId);
    const eventFetched = useEventsLoadDone([eventId]);
    const market = useMarketItems([marketId])?.[0];
    const marketFetched = useMarketsLoadDone([marketId]);
    const selection = market?.selectionMap?.[selectionId];
    const betslipSelectionState = useBetslipSelectionState(selectionId);
    const buttonState = getButtonState(betslipSelectionState);
    const selectionButtonConfig = useConfig('SelectionButton');
    const eventsMarketsSkeletonConfig = useConfig('EventMarketsSkeleton');
    const buttonConfig = useMemo(() => merge(selectionButtonConfig.view, styles), [selectionButtonConfig, styles]);
    const configHash = useSelector(selectVisualConfigHash);
    const onClick = useCallback(() => {
        const selectionData = {...selection, marketId};

        switch (buttonState) {
            case SelectionButtonTypes.STATES.INACTIVE:
                dispatch(
                    betslipActions.addSelection({
                        selection: betslipModelFactory.getSelection(selectionData),
                        parent: betslipModelFactory.getParent(event, selectionData),
                        source: SOURCE,
                    })
                );
                onChange({action: 'addSelection', selectionId: selectionData.id});

                return;
            case SelectionButtonTypes.STATES.ACTIVE:
                dispatch(
                    betslipActions.removeSelection({
                        selectionId: selectionData.id,
                        parent: betslipModelFactory.getParent(event, selectionData),
                        source: SOURCE,
                    })
                );
                onChange({action: 'removeSelection', selectionId: selectionData.id});

                return;
            case SelectionButtonTypes.STATES.PENDING:
            default:
                // do nothing
                return;
        }
    }, [buttonState, event, selection]);

    if (!eventId || !marketId || !selectionId) return null;

    if (!eventFetched || !marketFetched)
        return <SkeletonPart config={eventsMarketsSkeletonConfig.selectionPlaceholder} />;

    if (!event) return log.error(`Event with id "${eventId}" was not found.`);
    if (!market) return log.error(`Event with id "${eventId}" does not contain market with id "${marketId}".`);
    if (!selection)
        return log.error(`Market with id "${marketId}" does not contain selection with id "${selectionId}".`);

    return (
        <ApplicationContext key={configHash}>
            <AnalyticsContextExtender value={analyticsContext}>
                <SelectionButtonView
                    config={buttonConfig}
                    state={buttonState}
                    selection={selection}
                    event={event}
                    nameDisplay={SelectionButtonTypes.NAME_DISPLAY.HIDE}
                    onClick={onClick}
                />
            </AnalyticsContextExtender>
        </ApplicationContext>
    );
};

const analyticsContext = new AnalyticsContextBuilder().withAnalyticsPath('WidgetSelectionButton').build();

const useBetslipSelectionState = selectionId =>
    useSelector(state => betslipSelectors.selectSelectionState(selectionId, state));

const getButtonState = selectionState => {
    const {INACTIVE, PENDING, ACTIVE} = SelectionButtonTypes.STATES;

    if (!selectionState) {
        return INACTIVE;
    }

    return selectionState.isPending ? PENDING : ACTIVE;
};

WidgetSelectionButtonImpl.propTypes = {
    params: PropTypes.shape({
        eventId: PropTypes.string.isRequired,
        marketId: PropTypes.string.isRequired,
        selectionId: PropTypes.string.isRequired,
        styles: PropTypes.object,
        onChange: PropTypes.func,
    }).isRequired,
};

export const WidgetSelectionButton = StoreProviderWrapper('WidgetSelectionButton', WidgetSelectionButtonImpl);
